import React, { useEffect, useState } from 'react';
import { ShieldAlert, History, Loader2, XCircle, ShieldCheck, ShieldX, HelpCircle } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => {
  const stored = localStorage.getItem('ivory_user');
  try {
    return stored ? JSON.parse(stored).token : localStorage.getItem('token');
  } catch {
    return localStorage.getItem('token');
  }
};

export const authFetch = (path, options = {}) =>
  fetch(`${API_URL}/api${path}`, { ...options, headers: { Authorization: `Bearer ${getToken()}`, ...(options.headers || {}) } });

const severityClass = (s) =>
  s === 'Severe' || s === 'Life-threatening' ? 'bg-red-600 text-white' : s === 'Mild' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800';

export const AllergyBadges = ({ allergies = [], patientId }) => {
  if (!allergies.length) {
    return <span className="text-xs text-gray-400" data-testid={`allergy-none-${patientId}`}>No allergies recorded</span>;
  }
  const severe = allergies.some(a => a.severity === 'Severe' || a.severity === 'Life-threatening');
  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>
        <span
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold cursor-help ${severe ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'}`}
          data-testid={`allergy-badge-${patientId}`}
        >
          <ShieldAlert className="w-3 h-3" />
          {allergies.length === 1 ? allergies[0].allergen : `${allergies.length} Allergies`}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-3" data-testid={`allergy-hover-${patientId}`}>
        <p className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> Allergies</p>
        <div className="space-y-1.5">
          {allergies.map((a, i) => (
            <div key={i} className="flex items-start justify-between gap-2 text-xs">
              <div>
                <p className="font-medium text-gray-800">{a.allergen}</p>
                {a.reaction && <p className="text-gray-500">Reaction: {a.reaction}</p>}
              </div>
              <span className={`px-1.5 py-0.5 rounded ${severityClass(a.severity)}`}>{a.severity}</span>
            </div>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const FORMULARY_STYLE = {
  covered: { cls: 'bg-green-100 text-green-800', Icon: ShieldCheck },
  covered_pa: { cls: 'bg-amber-100 text-amber-800', Icon: ShieldAlert },
  not_covered: { cls: 'bg-red-100 text-red-700', Icon: ShieldX },
  unknown: { cls: 'bg-gray-100 text-gray-600', Icon: HelpCircle },
};

const formularyLabel = (r) =>
  r.status === 'not_covered' ? 'Not covered' : r.status === 'unknown' ? 'Coverage unknown' : `Tier ${r.tier} · ${r.copay}${r.priorAuth ? ' · PA' : ''}`;

export const FormularyDetails = ({ result }) => (
  <div className="text-xs space-y-1">
    <p className="font-semibold text-gray-800">{result.plan}</p>
    <p className="text-gray-600">{result.message}</p>
    {result.tier && <p>Tier {result.tier} · Est. copay {result.copay}</p>}
    {result.quantityLimit && <p>Quantity limit: {result.quantityLimit}</p>}
    {result.stepTherapy && <p className="text-amber-700">Step therapy required</p>}
    {result.alternatives?.length > 0 && <p>Alternatives: {result.alternatives.join(', ')}</p>}
    <p className="text-[10px] text-gray-400 pt-1">Sample formulary data - verify with the plan</p>
  </div>
);

export const FormularyBadge = ({ result, testId }) => {
  if (!result) return null;
  const { cls, Icon } = FORMULARY_STYLE[result.status] || FORMULARY_STYLE.unknown;
  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${cls}`} data-testid={testId}>
          <Icon className="w-3 h-3" /> {formularyLabel(result)}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-3"><FormularyDetails result={result} /></HoverCardContent>
    </HoverCard>
  );
};

export const FormularyCheck = ({ patientId, drugName }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!drugName || drugName.trim().length < 3) { setResult(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await authFetch(`/formulary/check?drug=${encodeURIComponent(drugName.trim())}&patientId=${patientId}`);
        const data = await res.json();
        setResult(data.result || null);
      } finally {
        setLoading(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [drugName, patientId]);
  if (loading) return <p className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking insurance formulary...</p>;
  if (!result) return null;
  const { cls } = FORMULARY_STYLE[result.status] || FORMULARY_STYLE.unknown;
  return <div className={`mt-2 p-2 rounded-lg ${cls}`} data-testid="add-med-formulary-result"><FormularyDetails result={result} /></div>;
};

const ACTION_STYLE = {
  created: 'bg-green-100 text-green-800', updated: 'bg-blue-100 text-blue-800',
  deleted: 'bg-red-100 text-red-700', discontinued: 'bg-gray-200 text-gray-700',
};

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));

const AuditEntry = ({ e }) => (
  <div className="border rounded-lg p-3" data-testid={`audit-entry-${e.id}`}>
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${ACTION_STYLE[e.action] || 'bg-gray-100'}`}>{e.action}</span>
      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">{e.entityType}</span>
      <span className="font-medium text-gray-800">{e.entityName}</span>
      <span className="ml-auto text-xs text-gray-500">{new Date(e.timestamp).toLocaleString()}</span>
    </div>
    <p className="text-xs text-gray-500 mt-1">By {e.performedBy?.name} ({e.performedBy?.email}){e.source !== 'manual' ? ` · via ${e.source.replace('_', ' ')}` : ''}</p>
    {e.action === 'updated' && e.changes?.length > 0 && (
      <table className="w-full mt-2 text-xs">
        <thead><tr className="text-gray-500"><th className="text-left">Field</th><th className="text-left">Before</th><th className="text-left">After</th></tr></thead>
        <tbody>
          {e.changes.map(c => (
            <tr key={c.field} className="border-t"><td className="py-1 font-medium">{c.field}</td><td className="py-1 text-red-600 line-through">{fmt(c.before)}</td><td className="py-1 text-green-700">{fmt(c.after)}</td></tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export const MedicationAuditModal = ({ patientId, onClose }) => {
  const [entries, setEntries] = useState(null);
  const [filter, setFilter] = useState('');
  useEffect(() => {
    authFetch(`/patients/${patientId}/medication-audit${filter ? `?entityType=${filter}` : ''}`)
      .then(r => r.json()).then(d => setEntries(d.entries || []));
  }, [patientId, filter]);
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="medication-audit-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gray-900 p-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#d4af37] flex items-center gap-2"><History className="w-5 h-5" /> Medication Audit Log</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="audit-close-btn"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="px-4 pt-3 flex gap-2">
          {[['', 'All'], ['medication', 'Medications'], ['allergy', 'Allergies']].map(([v, l]) => (
            <button key={l} onClick={() => setFilter(v)} data-testid={`audit-filter-${l.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-xs font-medium ${filter === v ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}>{l}</button>
          ))}
        </div>
        <div className="p-4 overflow-y-auto space-y-2">
          {entries === null ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> :
            entries.length === 0 ? <p className="text-center text-gray-500 py-8">No changes recorded yet</p> :
              entries.map(e => <AuditEntry key={e.id} e={e} />)}
        </div>
      </div>
    </div>
  );
};
